// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Turns measured cross-sections into the geometry and labels that get drawn. Each row gets one horizontal
 * dimension line below its conduits, with a witness line dropping onto it from each conduit centerline and
 * strut edge, all at the rack's single dimension station so the chain stays coplanar with the rack's edge.
 */

import type { MeasuredCrossSection } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/buildMeasuredCrossSection';
import { resolveRackFramePoint } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/rackFrame';
import { formatSpanFt } from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/rendering/formatSpanFt';
import type {
  ProjectedRackPipe,
  RackFrame,
  RackTier,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import type { THREE } from 'components/Autodesk/viewerThree';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { isInsignificant } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

const DIMENSION_LINE_RADIUS_FT = 0.012;
const WITNESS_LINE_RADIUS_FT = 0.006;
const TICK_RADIUS_FT = 0.008;

/** How far a witness line overshoots below the dimension line it crosses. */
const EXTENSION_OVERSHOOT_FT = 0.12;
/** How far below the dimension line a measurement label sits. */
const LABEL_OFFSET_FT = 0.12;
/** Length of the tick mark drawn where a witness line crosses the dimension line. */
const TICK_SIZE_FT = 0.05;
/** Spans this small are duplicate geometry rather than a real dimension, so they are not labeled. */
const MIN_LABELED_SPAN_FT = 0.01 / 12;

/** One point a witness line rises from: either a conduit, or a row's left/right strut edge. */
type RackWitness = { widthFt: number; pipe: ProjectedRackPipe | null };

declare const rackDimensionLabelIdSymbol: unique symbol;
/** Identifies one dimension label within a {@link RackDimensionPlan}. */
export type RackDimensionLabelId = string & { [rackDimensionLabelIdSymbol]: never };

/** A dimension label anchored to a world-space point, projected to pixels by the rendering manager. */
type RackDimensionLabelPlan = {
  rackDimensionLabelId: RackDimensionLabelId;
  /** The formatted measurement, e.g. `6-1/2"`. */
  text: string;
  /** The world-space point the label is centered on. */
  anchor: THREE.Vector3;
};
/** Everything needed to draw the dimensions for a set of racks: overlay geometry plus HTML labels. */
export type RackDimensionPlan = {
  segments: RackDimensionSegment[];
  labels: RackDimensionLabelPlan[];
};

/** A straight line drawn in the viewer's dimension overlay. */
type RackDimensionSegment = {
  start: THREE.Vector3;
  end: THREE.Vector3;
  /** Cylinder radius in feet. Segments are widened on screen so they stay visible when zoomed out. */
  radiusFt: number;
};

const offsetAlong = (point: THREE.Vector3, direction: THREE.Vector3, distanceFt: number): THREE.Vector3 =>
  point.clone().add(direction.clone().multiplyScalar(distanceFt));

const midpointOf = (start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3 =>
  start.clone().add(end).multiplyScalar(0.5);

/** A segment, or nothing at all when its endpoints coincide. */
const toSegment = (start: THREE.Vector3, end: THREE.Vector3, radiusFt: number): RackDimensionSegment[] =>
  isInsignificant(start.distanceTo(end)) ? [] : [{ start, end, radiusFt }];

/** A short diagonal tick through `point`, in the rack's width/height plane. */
const buildTick = (point: THREE.Vector3, frame: RackFrame): RackDimensionSegment[] => {
  const diagonal = frame.widthDirection
    .clone()
    .add(frame.heightDirection)
    .multiplyScalar(TICK_SIZE_FT / 2);
  return toSegment(point.clone().add(diagonal), point.clone().sub(diagonal), TICK_RADIUS_FT);
};

/**
 * The witness points across a row, left to right: left strut edge, each conduit, right strut edge.
 * Not deduplicated: the list stays index-aligned with {@link RackTier.chainFt}.
 */
const buildRowWitnesses = (tier: RackTier): RackWitness[] => [
  { widthFt: tier.minWidthFt, pipe: null },
  ...tier.pipes.map((pipe) => ({ widthFt: pipe.widthFt, pipe })),
  { widthFt: tier.maxWidthFt, pipe: null },
];

/** Builds the {@link RackDimensionLabelId} for one span of a row's dimension chain. */
const makeRackChainLabelId = (crossSectionIndex: number, tierIndex: number, spanIndex: number) =>
  `section-${crossSectionIndex}-tier-${tierIndex}-span-${spanIndex}` as RackDimensionLabelId;

const buildRowPlan = (
  tier: RackTier,
  frame: RackFrame,
  stationFt: number,
  crossSectionIndex: number,
  tierIndex: number,
): RackDimensionPlan => {
  // A conduit's witness starts at its centerline; a strut edge has no conduit, so it starts at the row's
  // height. Both sit at the same station, keeping the chain coplanar with the edge of the rack.
  const witnessAnchorAt = ({ widthFt, pipe }: RackWitness) =>
    resolveRackFramePoint(frame, { widthFt, heightFt: pipe?.heightFt ?? tier.referenceHeightFt, stationFt });
  const dimLinePointAt = (widthFt: number) =>
    resolveRackFramePoint(frame, { widthFt, heightFt: tier.dimensionLineHeightFt, stationFt });

  const witnesses = buildRowWitnesses(tier);
  const witnessSegments = witnesses.flatMap((witness) => {
    const dimLinePoint = dimLinePointAt(witness.widthFt);
    return [
      ...toSegment(witnessAnchorAt(witness), dimLinePoint, WITNESS_LINE_RADIUS_FT),
      ...toSegment(
        dimLinePoint,
        offsetAlong(dimLinePoint, frame.heightDirection, -EXTENSION_OVERSHOOT_FT),
        WITNESS_LINE_RADIUS_FT,
      ),
      ...buildTick(dimLinePoint, frame),
    ];
  });

  const firstWitness = witnesses[0];
  const lastWitness = witnesses[witnesses.length - 1];
  const dimensionLine =
    isNotNil(firstWitness) && isNotNil(lastWitness)
      ? toSegment(dimLinePointAt(firstWitness.widthFt), dimLinePointAt(lastWitness.widthFt), DIMENSION_LINE_RADIUS_FT)
      : [];

  const labels = tier.chainFt.flatMap((spanFt, spanIndex): RackDimensionLabelPlan[] => {
    const start = witnesses[spanIndex];
    const end = witnesses[spanIndex + 1];
    if (isNil(start) || isNil(end) || isInsignificant(spanFt, MIN_LABELED_SPAN_FT)) return [];

    const spanMidpoint = midpointOf(dimLinePointAt(start.widthFt), dimLinePointAt(end.widthFt));
    return [
      {
        rackDimensionLabelId: makeRackChainLabelId(crossSectionIndex, tierIndex, spanIndex),
        text: formatSpanFt(spanFt),
        anchor: offsetAlong(spanMidpoint, frame.heightDirection, -LABEL_OFFSET_FT),
      },
    ];
  });

  return { segments: [...witnessSegments, ...dimensionLine], labels };
};

/** Build the overlay geometry and labels for every measured cross-section. */
export const buildRackDimensionPlan = (crossSections: MeasuredCrossSection[]): RackDimensionPlan => {
  const plans = crossSections.flatMap(({ frame, dimensionStationFt, tiers }, crossSectionIndex) =>
    tiers.map((tier, tierIndex) => buildRowPlan(tier, frame, dimensionStationFt, crossSectionIndex, tierIndex)),
  );

  return {
    segments: plans.flatMap(({ segments }) => segments),
    labels: plans.flatMap(({ labels }) => labels),
  };
};
